/**
 * Circuit Card Modal Component
 * Full-screen modal displaying circuit details via Nomade Aventure iframe
 */

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import {
  Phone,
} from 'lucide-react';
import type { CircuitCardModalProps } from '@/types';
import { useUIStore } from '@/stores/uiStore';

export function CircuitCardModal({
  circuit,
  isOpen,
  onClose,
}: CircuitCardModalProps) {
  const setIsContactModalOpen = useUIStore((state) => state.setIsContactModalOpen);

  if (!circuit) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] w-[90vw] h-[96vh] p-0 overflow-hidden bg-white border-none rounded-2xl fixed top-[2vh] left-1/2 -translate-x-1/2 translate-y-0 data-[state=open]:slide-in-from-top-4">
        <div className="sr-only">
          <DialogTitle>{circuit.name}</DialogTitle>
          <DialogDescription>Détails du circuit {circuit.name}</DialogDescription>
        </div>
        {/* Iframe displaying the Nomade Aventure page */}
        <div className="w-full h-full relative">
          <iframe
            src={circuit.url}
            title={circuit.name}
            className="w-full h-full border-0"
            // Sandbox attributes for security to prevent the iframe from taking over the top window
            // allow-same-origin: allows cookies/session
            // allow-scripts: allows JS
            // allow-popups: allows opening new tabs
            // allow-forms: allows submissions
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        </div>

        {/* Floating CTA Container */}
        <div className="absolute bottom-6 left-0 right-0 z-50 flex justify-center pointer-events-none">
          {/* Pointer events auto on button only so we can click through the container */}
          <div className="pointer-events-auto shadow-2xl rounded-full">
            <Button
              variant="default"
              size="lg"
              className="bg-black/60 hover:bg-nomade-accent text-white font-medium py-6 px-8 rounded-full shadow-2xl backdrop-blur-md border border-white/20 hover:border-nomade-accent transition-all duration-300 transform hover:scale-105 group"
              onClick={() => {
                onClose();
                setIsContactModalOpen(true);
              }}
            >
              <Phone className="h-5 w-5 mr-3 text-nomade-accent group-hover:text-white transition-colors" />
              <span className="tracking-wide">Prendre RDV</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
